import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ActionIconDef, Grid, GridActionIconClickEvent, GridConfig, GridExportRequestedEvent, GridHyperlinkClickEvent, GridRowCommitEvent } from '@inavista/components/grid';
import { race, timer } from 'rxjs';
import { first } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { ModuleNames } from 'projects/service/src/app/shared/models/app.model';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { exportInavistaGrid } from 'projects/service/src/app/shared/utils/inavista-grid-export';
import { statusPillColor } from 'projects/service/src/app/shared/utils/status-pill-colors';
import { CycleCountService } from './cycle-counts.service';
import { actionDisabledReason, ADD_CYCLE_COUNT_ACTION, buildCycleCountsActionIcons, buildCycleCountsGridConfig } from './cycle-counts.grid.config';

// Cycle Counts page: one @inavista/components grid (api/grid/cycle-counts). The cycle count popup, the Add Cycle Count
// popup and the comments panel are the ones the old grid opened. Eager, not OnPush: the cycle count popup and the add
// popup are Eager components that load their own data and never mark themselves for check; under an OnPush host they
// stay on their spinner (DONE.md "popups that never redrew under an OnPush host").
@Component({
    selector: 'app-cycle-counts',
    templateUrl: './cycle-counts.component.html',
    styleUrls: ['./cycle-counts.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CycleCountComponent implements OnInit, AfterViewInit {
  private readonly commonService = inject(CommonService);
  private readonly cycleCountService = inject(CycleCountService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  // Null until the user's role claims are known (Add Cycle Count and the Start Date edit depend on them).
  readonly gridConfig = signal<GridConfig | null>(null);
  readonly actionIcons = signal<ActionIconDef[]>(buildCycleCountsActionIcons());
  readonly showLoader = signal(false);
  // The cycle count popup on one tab: the row clicked, or (a comment notification) only the id in the route.
  readonly cycleCountPopup = signal<{ row: unknown; cycleCountId: string | null; tab: string; showComments: boolean } | null>(null);
  readonly showAddPopup = signal(false);
  // Today, the earliest start date the picker offers (as before).
  readonly today = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })();

  @ViewChild('cycleCountGrid') grid: Grid;
  @ViewChild('cycleCountGridPage') cycleCountGridPage: ElementRef<HTMLElement>;
  @ViewChild('startDateEditor', { static: true }) startDateEditor: TemplateRef<unknown>;

  // The grid fills the window from where it starts down to the page frame's bottom padding, as Claims.
  private static readonly BottomFramePx = 15;
  readonly gridPageHeight = signal(window.innerHeight - 120);

  @HostListener('window:resize')
  fitGridToWindow() {
    const top = this.cycleCountGridPage?.nativeElement.getBoundingClientRect().top;
    if (top === undefined) return;
    this.gridPageHeight.set(Math.max(300, Math.floor(window.innerHeight - top - CycleCountComponent.BottomFramePx)));
  }

  ngOnInit(): void {
    // On a full page load the page can open before the role claims arrive (as Claims): the grid is built once they
    // are there. A role with no rule at all never fills them: the grid then opens after 5 s with nothing role-gated.
    if (Object.keys(this.commonService.roleClaims ?? {}).length) this.initGrid();
    else race(this.commonService.getIsClaimLoaded().pipe(first()), timer(5000))
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.initGrid());

    // cycle-counts/:id/:showComments (a comment notification) opens that cycle count with its comments; the popup
    // reads the id from the route.
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      if (params.get('id') && params.get('showComments')) {
        this.cycleCountPopup.set({ row: null, cycleCountId: params.get('id'), tab: 'details', showComments: params.get('showComments') === 'showComments' });
      }
    });
  }

  private initGrid() {
    this.gridConfig.set(buildCycleCountsGridConfig({
      roleClaims: (this.commonService.roleClaims ?? {}) as Record<string, { visible?: boolean; edit?: boolean }>,
      statusColor: statusPillColor(s => this.commonService.getStatusClass(s)),
      startDateEditor: this.startDateEditor,
    }));
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.fitGridToWindow());
  }

  // Re-reads one cycle count in place, so the user keeps their place; the whole list when none is known.
  protected refresh(cycleCountId?: string | null) {
    if (!this.grid) return;
    if (!cycleCountId) { this.grid.refresh(); return; }
    this.grid.refreshRow(cycleCountId).catch(() =>
      this.commonService.showNotification('error', 'The list could not be updated. Please refresh the page.'));
  }

  onToolbarActionClick(event: { key: string }) {
    if (event.key === ADD_CYCLE_COUNT_ACTION) this.showAddPopup.set(true);
  }

  // Same file as before: the visible columns of the rows loaded, "Cycle count(<date>).xlsx".
  onExportRequested(event: GridExportRequestedEvent) {
    exportInavistaGrid(this.grid, 'Cycle count', event.rows);
  }

  onHyperlinkClick(event: GridHyperlinkClickEvent) {
    this.openCycleCount(event.row, 'details');
  }

  onActionIconClick(event: GridActionIconClickEvent) {
    const row: any = event.row;
    const reason = actionDisabledReason(event.iconKey, row);
    if (reason) { this.commonService.showNotification('info', reason); return; }
    switch (event.iconKey) {
      case 'schedule': this.schedule(row); return;
      case 'delete': this.confirmDelete(row.cycleCountId); return;
      case 'costAnalysis': this.openCycleCount(row, 'costAnalysis'); return;
      case 'attachments': this.openCycleCount(row, 'attachments'); return;
      case 'history': this.openCycleCount(row, 'history'); return;
      case 'comments':
        // Opened after this click: the comments panel closes on any document click outside it (as Claims).
        setTimeout(() => {
          this.commonService.globalComments = {
            grid: { refresh: () => this.refresh(row.cycleCountId) } as any,
            module: ModuleNames.cycleCount, moduleId: row.cycleCountId, showComments: true,
          };
        });
        return;
      default: this.openCycleCount(row, 'details');
    }
  }

  private openCycleCount(row: any, tab: string) {
    this.cycleCountPopup.set({ row, cycleCountId: row?.cycleCountId ?? null, tab, showComments: false });
  }

  // The popup can change the cycle count (status, dates, counts): re-read it.
  onCycleCountClose() {
    const cycleCountId = this.cycleCountPopup()?.cycleCountId;
    this.cycleCountPopup.set(null);
    this.refresh(cycleCountId);
  }

  // As before: refused while the cycle count's technician has open exceptions; otherwise the status becomes Scheduled.
  private schedule(row: any) {
    if (row.openExceptionCount > 0) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Please adjust all exceptions before scheduling the cycle count!', confirmButtonColor: '#479e47' });
      return;
    }
    this.showLoader.set(true);
    this.cycleCountService.updateCycleCountStatus({ cycleCountId: row.cycleCountId, status: 'Scheduled' })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.showLoader.set(false);
        this.commonService.showNotification(res.isSuccessful ? 'success' : 'error', res.message);
        if (res.isSuccessful) this.refresh(row.cycleCountId);
      },
      error: err => {
        this.showLoader.set(false);
        this.commonService.showNotification('error', err?.message ?? 'The cycle count could not be scheduled.');
      },
    });
  }

  private confirmDelete(cycleCountId: string) {
    this.commonService.showConfirmation(`Are you sure? ${cycleCountId} will be deleted.`).then(result => {
      if (!result.isConfirmed) return;
      this.cycleCountService.deleteCycleCount(cycleCountId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        // The old page said "deleted" whatever the answer; the API deletes only an Open cycle count.
        next: (res: any) => {
          if (res?.isSuccessful === false) this.commonService.showNotification('error', res.message);
          else this.commonService.showNotification('warning', `${cycleCountId} has been deleted.`);
          this.refresh(cycleCountId);
        },
        error: err => this.commonService.showNotification('error', err?.message ?? `${cycleCountId} could not be deleted.`),
      });
    });
  }

  // A start date saved in the grid (Enter or the row's Save): the same call as before, which also sets the status to
  // Planning; the row is then read again (status, duration).
  onRowCommit(commit: GridRowCommitEvent) {
    const row: any = commit.row;
    const value = row.startDate ? new Date(row.startDate) : null;
    if (value && isNaN(value.getTime())) {
      this.grid.saveFailed(commit, { kind: 'validation', column: 'startDate', message: 'Invalid start date. Please pick a valid date.' });
      return;
    }
    this.showLoader.set(true);
    this.cycleCountService.updateCycleCountStartDate({ cycleCountId: row.cycleCountId, status: 'Planning', startDate: value ? value.toISOString() : null })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (res: any) => {
          this.showLoader.set(false);
          if (!res?.isSuccessful) {
            this.grid.saveFailed(commit, { kind: 'validation', message: res?.message ?? 'The start date could not be saved.' });
            return;
          }
          this.commonService.showNotification('success', res.message);
          this.grid.saveSucceeded(commit, { ...commit.row, status: 'Planning' });
          this.refresh(row.cycleCountId);
        },
        error: err => {
          this.showLoader.set(false);
          this.grid.saveFailed(commit, { kind: 'validation', message: err?.message ?? 'The start date could not be saved.' });
        },
      });
  }
}
